import React, { useCallback, useMemo, useState, type PropsWithChildren, Fragment } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment, useMutation } from 'react-relay';
import editMessages from '@atlassian/jira-common-components-inline-edit/src/messages.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useInlineEditFieldInjections } from '@atlassian/jira-issue-field-injections/src/controllers/inline-edit-injections-context/index.tsx';
import { useFieldInlineEditActions } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/index.tsx';
import type { OnSubmitCallbacks } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/types.tsx';
import type { ValidationFieldProps } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/field-inline-edit-lite/types.tsx';
import { FieldInlineEditLiteWithEntryPoint } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/index.tsx';
import { SingleUserPickerWithProfileCardReadView } from '@atlassian/jira-issue-field-single-user-picker-readview-full/src/ui/single-user-picker/index.tsx';
import type { AggUser } from '@atlassian/jira-issue-user-picker-edit-view/src/common/types.tsx';
import SingleUserPickerEditViewEntryPoint from '@atlassian/jira-issue-user-picker-edit-view/src/entrypoint.tsx';
import type { SingleUserPickerEditViewProps } from '@atlassian/jira-issue-user-picker-edit-view/src/ui/single-user-picker/types.tsx';
import { transformAggToUserOption } from '@atlassian/jira-issue-user-picker-edit-view/src/utils/transform-agg-to-user-option/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { createMutationErrorHandlers } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/utils/extract-error-message.tsx';
import type { singleUserPicker_issueFieldSingleUser_SingleUserPickerField_Mutation as UserPickerMutation } from './__generated__/singleUserPicker_issueFieldSingleUser_SingleUserPickerField_Mutation.graphql';
import type { singleUserPicker_issueFieldSingleUserPickerInlineEditFull_SingleUserPickerInlineEditView_fragmentRef$key as UserPickerFieldFragment } from './__generated__/singleUserPicker_issueFieldSingleUserPickerInlineEditFull_SingleUserPickerInlineEditView_fragmentRef.graphql';
import type { singleUserPicker_issueFieldSingleUserPickerInlineEditFull_SingleUserPickerInlineEditViewWithIsEditable_fragmentRef$key as UserPickerIsEditableFragment } from './__generated__/singleUserPicker_issueFieldSingleUserPickerInlineEditFull_SingleUserPickerInlineEditViewWithIsEditable_fragmentRef.graphql';
import messages from './messages.tsx';
import type {
	SingleUserPickerInlineEditViewWithIsEditableProps,
	SingleUserPickerInlineEditViewProps,
} from './types.tsx';
import { useSingleUserPickerValidator } from './utils.tsx';

const isEqualUser = (a: AggUser | null, b: AggUser | null) => a?.id === b?.id;

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="issue-field-single-user-picker-read-view" mode="list">
			{children}
		</UFOSegment>
	),
	Fragment,
);

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components. This variant allows
 * consumers to define their own value to determine whether the field is editable.
 *
 * In most cases consumers should use `SingleUserPickerInlineEditView` which will enforce that the user has permission to
 * edit the field within the issue view. However, this component can be used for experiences that have differing
 * permissions or want finer control for how this data is retrieved, e.g. lazy loading editability.
 *
 * @param props [SingleUserPickerInlineEditViewWithIsEditableProps](./types.tsx)
 */
export const SingleUserPickerInlineEditViewWithIsEditable = ({
	attributes,
	spacing = 'compact',
	editViewPopup,
	editViewPopupAlignBlock,
	editViewPopupWidth,
	editViewPopupTarget,
	fragmentRef,
	isEditing: startWithEditViewOpen = false,
	menuPosition,
	onSubmit,
	onSubmitFailed,
	onSubmitSucceeded,
	onCancel,
	onEdit,
	isEditable,
	readViewFitContainerHeight,
	noneOptionLabel,
	profileCardAction,
	portalElement,
	shouldRenderHoverPopover,
}: SingleUserPickerInlineEditViewWithIsEditableProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const data = useFragment<UserPickerIsEditableFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment singleUserPicker_issueFieldSingleUserPickerInlineEditFull_SingleUserPickerInlineEditViewWithIsEditable_fragmentRef on JiraSingleSelectUserPickerField {
				id
				fieldId
				type
				name
				...singleUserPicker_issueFieldSingleUserPickerReadviewFull_SingleUserPickerWithProfileCardReadView_fragmentRef
				user {
					accountId
					name
					picture
					accountStatus
					id
				}
				fieldConfig {
					isRequired
				}
			}
		`,
		fragmentRef,
	);

	const { overriding } = useInlineEditFieldInjections();

	const { id: uniqueFieldId, fieldId, type, name, user, fieldConfig } = data;
	const isFieldRequired = useMemo(
		() => fieldConfig?.isRequired ?? false,
		[fieldConfig?.isRequired],
	);

	const fieldName = useMemo(() => overriding.overrideLabel(name), [name, overriding]);

	const isFieldEditable = useMemo(
		() => overriding.overrideIsEditable(isEditable),
		[isEditable, overriding],
	);

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commit] = useMutation<UserPickerMutation>(graphql`
		mutation singleUserPicker_issueFieldSingleUser_SingleUserPickerField_Mutation(
			$input: JiraUpdateSingleSelectUserPickerFieldInput!
		) @raw_response_type {
			jira @optIn(to: ["JiraIssueFieldMutations"]) {
				updateSingleSelectUserPickerField(input: $input) {
					success
					errors {
						message
					}
					field {
						user {
							accountId
							name
							picture
							accountStatus
							id
						}
					}
				}
			}
		}
	`);

	const initialValue = user ?? null;
	const [updatedValue, setUpdatedValue] = useState<AggUser | null>(initialValue);

	const handleSubmit = useCallback(
		(newUser: AggUser | null, { onSuccess, onFail }: OnSubmitCallbacks) => {
			onSubmit?.(newUser);
			const onCompleted = fg('thor_inline-edit-actions-error-extraction')
				? createMutationErrorHandlers({ onSuccess, onFail })
				: // eslint-disable-next-line @typescript-eslint/no-explicit-any
					(response: any) => {
						if (response.jira?.updateSingleSelectUserPickerField?.success) {
							onSuccess();
						} else {
							onFail();
						}
					};

			commit({
				variables: {
					input: {
						id: uniqueFieldId,
						operation: {
							operation: 'SET',
							id: newUser?.id ?? null,
						},
					},
				},
				onCompleted,
				onError(error) {
					onFail(error);
				},
				optimisticResponse: {
					jira: {
						updateSingleSelectUserPickerField: {
							success: true,
							errors: null,
							field: {
								id: uniqueFieldId,
								user: newUser
									? {
											id: newUser.id,
											accountId: newUser.accountId,
											accountStatus: newUser.accountStatus,
											name: newUser.name,
											picture: newUser.picture,
											__typename: 'AtlassianAccountUser',
										}
									: null,
							},
						},
					},
				},
			});
		},
		[commit, uniqueFieldId, onSubmit],
	);

	const validator = useSingleUserPickerValidator(fieldName, isFieldRequired);

	const {
		handleCancel,
		handleEdit,
		handleConfirm,
		handleChangeAndConfirm,
		handleCopy,
		handleCut,
		handlePaste,
		hasServerValidationError,
		invalidMessage,
		isEditing,
	} = useFieldInlineEditActions({
		attributes,
		fieldId,
		fieldName,
		fieldType: type,
		initialValue,
		isValueEqual: isEqualUser,
		onCancel,
		onEdit,
		onSubmit: handleSubmit,
		onSubmitSucceeded,
		onSubmitFailed,
		onUpdateValue: setUpdatedValue,
		startWithEditViewOpen,
		updatedValue,
		validator,
		clipboardComponentType: 'singleUser',
	});

	// #region Read view
	const finalNoneOptionLabel = noneOptionLabel ?? formatMessage(messages.noneOption);
	const renderReadView = useCallback(
		() => (
			<MaybeUFOSegment>
				<SingleUserPickerWithProfileCardReadView
					fragmentRef={data}
					emptyUserLabel={finalNoneOptionLabel}
					profileCardAction={profileCardAction}
					shouldRenderHoverPopover={shouldRenderHoverPopover}
					fieldId={fieldId}
				/>
			</MaybeUFOSegment>
		),
		[data, finalNoneOptionLabel, profileCardAction, shouldRenderHoverPopover, fieldId],
	);
	// #endregion

	const getEditViewProps = (fieldProps: ValidationFieldProps): SingleUserPickerEditViewProps => ({
		...fieldProps,
		fieldId: uniqueFieldId,
		autoFocus: true,
		value: updatedValue ? transformAggToUserOption(updatedValue) : null,
		onChange: handleChangeAndConfirm,
		onClear: () => handleChangeAndConfirm(null),
		spacing,
		noneOptionLabel: finalNoneOptionLabel,
		includeNoneOption: !isFieldRequired,
		isClearable: !isFieldRequired,
		menuPosition,
		portalElement,
		ariaLabel: fieldName,
		isInvalid: !!invalidMessage,
	});

	const editButtonLabel = formatMessage(editMessages.extendedEditButtonLabel, {
		inputFieldValue: initialValue?.name ?? finalNoneOptionLabel,
		fieldName,
	});

	return (
		<FieldInlineEditLiteWithEntryPoint
			editViewPopup={editViewPopup}
			editViewPopupAlignBlock={editViewPopupAlignBlock}
			editViewPopupWidth={editViewPopupWidth}
			editViewPopupTarget={editViewPopupTarget}
			editViewEntryPoint={SingleUserPickerEditViewEntryPoint}
			editViewEntryPointParams={{}}
			getEditViewProps={getEditViewProps}
			fieldName={fieldName}
			hasUnsubmittedChanges={hasServerValidationError}
			invalidMessage={invalidMessage}
			isEditing={isEditing}
			isEditable={isFieldEditable}
			onCancel={handleCancel}
			onConfirm={handleConfirm}
			onEdit={handleEdit}
			onCopy={handleCopy}
			onCut={handleCut}
			onPaste={handlePaste}
			readView={renderReadView}
			hideActionButtons
			editButtonLabel={editButtonLabel}
			readViewFitContainerHeight={readViewFitContainerHeight}
			disableReadViewAppearanceOnHoverOnly={fieldId === 'reporter'}
		/>
	);
};

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components.
 *
 * @param props [SingleUserPickerInlineEditViewProps](./types.tsx)
 */
export const SingleUserPickerInlineEditView = ({
	fragmentRef,
	...props
}: SingleUserPickerInlineEditViewProps): React.JSX.Element => {
	const data = useFragment<UserPickerFieldFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment singleUserPicker_issueFieldSingleUserPickerInlineEditFull_SingleUserPickerInlineEditView_fragmentRef on JiraSingleSelectUserPickerField {
				...singleUserPicker_issueFieldSingleUserPickerInlineEditFull_SingleUserPickerInlineEditViewWithIsEditable_fragmentRef
				fieldConfig {
					isEditable
				}
			}
		`,
		fragmentRef,
	);

	return (
		<SingleUserPickerInlineEditViewWithIsEditable
			{...props}
			fragmentRef={data}
			isEditable={data.fieldConfig?.isEditable ?? false}
		/>
	);
};
